import React, { useCallback, useEffect, useState } from 'react';
import { FiCopy, FiGift, FiUsers, FiCheck } from 'react-icons/fi';
import { useApi } from '../hooks/useApi';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function ReferralPanel({ modoOscuro, box, textPri, textSec, textMuted }) {
  const { secureFetch } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await secureFetch('/api/referrals/me');
      if (!res.ok) throw new Error('No se pudo cargar referidos');
      setData(await res.json());
    } catch (err) {
      setError(err.message || 'Error al cargar referidos');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [secureFetch]);

  useEffect(() => {
    load();
  }, [load]);

  const copyText = async (label, value) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied(''), 2000);
    } catch {
      setCopied('');
    }
  };

  if (loading) {
    return (
      <div className={`${box} rounded-2xl border p-4 animate-pulse`}>
        <div className={`h-4 w-40 rounded mb-3 ${modoOscuro ? 'bg-gray-800' : 'bg-gray-200'}`} />
        <div className={`h-10 rounded ${modoOscuro ? 'bg-gray-800' : 'bg-gray-200'}`} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${box} rounded-2xl border p-4`}>
        <p className={`text-sm ${modoOscuro ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const stats = data.stats || {};

  return (
    <div className={`${box} rounded-2xl border overflow-hidden`}>
      <div className={`px-4 py-3 border-b ${modoOscuro ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
            modoOscuro ? 'bg-[#1F80FF]/20 text-[#7eb8ff]' : 'bg-[#1F80FF]/10 text-[#004AAD]'
          }`}>
            <FiGift className="w-4 h-4" />
          </div>
          <div>
            <h2 className={`text-sm font-semibold ${textPri}`}>Invitá y ganá registros</h2>
            <p className={`text-xs ${textMuted}`}>
              Vos recibís +{data.referrerBonusDocuments} y tu invitado +{data.referredBonusDocuments} al registrarse.
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        <div className="grid grid-cols-3 gap-2">
          <div className={`rounded-xl px-3 py-2.5 text-center ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
            <div className={`text-lg font-semibold tabular-nums ${textPri}`}>{stats.total || 0}</div>
            <div className={`text-[10px] uppercase ${textMuted}`}>Invitados</div>
          </div>
          <div className={`rounded-xl px-3 py-2.5 text-center ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
            <div className={`text-lg font-semibold tabular-nums ${textPri}`}>{stats.rewarded || 0}</div>
            <div className={`text-[10px] uppercase ${textMuted}`}>Activados</div>
          </div>
          <div className={`rounded-xl px-3 py-2.5 text-center ${modoOscuro ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
            <div className={`text-lg font-semibold tabular-nums ${textPri}`}>{stats.bonusDocumentsEarned || 0}</div>
            <div className={`text-[10px] uppercase ${textMuted}`}>Bonus total</div>
          </div>
        </div>

        <div>
          <label className={`block text-[10px] font-semibold uppercase tracking-wide mb-1.5 ${textMuted}`}>
            Tu código
          </label>
          <div className="flex gap-2">
            <div className={`flex-1 rounded-lg border px-3 py-2 text-sm font-mono font-semibold ${
              modoOscuro ? 'border-gray-700 bg-gray-800 text-white' : 'border-gray-200 bg-white text-gray-900'
            }`}>
              {data.code}
            </div>
            <button
              type="button"
              onClick={() => copyText('code', data.code)}
              className={`inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs font-semibold ${
                modoOscuro ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              {copied === 'code' ? <FiCheck className="w-3.5 h-3.5" /> : <FiCopy className="w-3.5 h-3.5" />}
              {copied === 'code' ? 'Copiado' : 'Copiar'}
            </button>
          </div>
        </div>

        <div>
          <label className={`block text-[10px] font-semibold uppercase tracking-wide mb-1.5 ${textMuted}`}>
            Link para compartir
          </label>
          <div className="flex gap-2">
            <div className={`flex-1 rounded-lg border px-3 py-2 text-xs truncate ${
              modoOscuro ? 'border-gray-700 bg-gray-800 text-gray-300' : 'border-gray-200 bg-white text-gray-700'
            }`}>
              {data.shareUrl}
            </div>
            <button
              type="button"
              onClick={() => copyText('link', data.shareUrl)}
              className={`shrink-0 inline-flex items-center gap-1 rounded-lg border px-3 py-2 text-xs font-semibold ${
                modoOscuro ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              {copied === 'link' ? <FiCheck className="w-3.5 h-3.5" /> : <FiCopy className="w-3.5 h-3.5" />}
              {copied === 'link' ? 'Listo' : 'Copiar link'}
            </button>
          </div>
        </div>

        {data.referrals?.length > 0 && (
          <div>
            <div className={`flex items-center gap-1.5 mb-2 ${textSec}`}>
              <FiUsers className="w-3.5 h-3.5" />
              <span className="text-xs font-semibold">Últimos invitados</span>
            </div>
            <ul className={`divide-y rounded-xl border overflow-hidden ${
              modoOscuro ? 'border-gray-800 divide-gray-800' : 'border-gray-100 divide-gray-100'
            }`}>
              {data.referrals.map((row) => (
                <li key={row.id} className="px-3 py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${textPri}`}>{row.referredName}</p>
                    <p className={`text-[11px] truncate ${textMuted}`}>{row.referredEmail || formatDate(row.createdAt)}</p>
                  </div>
                  <span className={`shrink-0 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${
                    row.status === 'rewarded'
                      ? (modoOscuro ? 'bg-green-900/40 text-green-200' : 'bg-green-50 text-green-700')
                      : (modoOscuro ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-600')
                  }`}>
                    {row.status === 'rewarded' ? 'Activado' : 'Pendiente'}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
